import * as Yup from "yup";

import { AccountPeriodsNames, wrapClick } from "utils";
import { Modal, RegularSelectInput, Show } from "components";
import {
  SendPTreatyCreditAndStatementNote,
  SendPTreatyCreditAndStatementNoteVariables,
} from "_graphql/mutation/__generated__/SendPTreatyCreditAndStatementNote";
import { useMatch, useSearch } from "react-location";
import { useTreaty, useTreatyReinsurer } from "hooks/data/use-treaties";

import { LocationGenerics } from "router/location";
import { SendEmailForm } from "components/forms";
import _ from "lodash";
import clsx from "clsx";
import { sendPTreatyCreditAndStatementNote } from "_graphql/visal/mutations/treaty";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";

export const validationSchema = Yup.object().shape({
  message: Yup.string().required("Message is required"),
  subject: Yup.string().required("Subject is required"),
});

export default function SendDocumentsContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const { params } = useMatch<LocationGenerics>();
  const searchParams = useSearch<LocationGenerics>();
  const { treaty, loading: gettingTreaty } = useTreaty({
    treatyId: params.treaty || "",
  });
  const [create, { loading }] = useMutation<
    SendPTreatyCreditAndStatementNote,
    SendPTreatyCreditAndStatementNoteVariables
  >(sendPTreatyCreditAndStatementNote, {
    refetchQueries: ["Treaty"],
  });
  const { participant, loading: fetchingParticipant } = useTreatyReinsurer({
    participationId: searchParams?.id || "",
  });
  const portFolioCond = Boolean(
    treaty?.treaty_accounts && treaty?.treaty_accounts?.length >= 4
  );
  const isProportional = searchParams?.treaty_type === "PROPORTIONAL";

  const form = useFormik<any>({
    initialValues: {
      copied_emails: [],
      subject: "",
      message: "",
      include_attachment: false,
      should_send: 0,
      doc_type: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (variables) => {
      await create({
        variables: {
          docType: variables.doc_type.includes("All")
            ? ["0", "1"]
            : [variables.doc_type],
          treaty_account_id:
            variables.statement_type === "All"
              ? treaty?.treaty_accounts?.map((el: any) => el?.treaty_account_id)
              : [variables.statement_type],
          participant_id: searchParams?.id || "",
          treaty_id: params.treaty || "",
          email_component: {
            attachments:
              variables.attachments?.map((el: any) => el?.file_path) || [],
            email_content: variables.message,
            copied_email: variables.copied_emails?.map((el: any) => el) || [],
            subject: variables.subject,
          },
        },
      }).then(({ data }) => {
        if (data?.sendPTreatyCreditAndStatementNote) {
          toast.success("Emails sent successfully");
          refetch?.();
          form.resetForm();
        } else {
          toast.error("Failed to send emails");
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={fetchingParticipant || gettingTreaty}
      title={"Send Treaty Closing Note"}
      description="Draft an email to send to the participant(s) of this treaty."
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Sending emails..." : "Send Email"}
          </button>
        </>
      )}
    >
      <SendEmailForm
        form={form}
        topForm={
          <div className="">
            <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-4 mb-4">
              <p className="text-sm  text-red-800 rounded-md">
                This session will send a copy of the TREATY CLOSING NOTE to{" "}
                {participant?.reinsurer?.re_company_name || "N/A"}.
              </p>
              <p className="text-sm  text-red-800 rounded-md">
                <strong>N.B:</strong> You can include any number of EMAILs.
                Press ENTER OR TAB key to add more
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
              <div className="grid grid-cols-2 gap-3">
                <div
                  className={clsx({
                    // "col-span-1": !portFolioCond,
                    "col-span-2": form.values.doc_type?.includes(
                      "Portfolio Statement"
                    ),
                  })}
                >
                  <RegularSelectInput
                    id="doc_type"
                    label="Document Type"
                    placeholder="Select Document Type"
                    options={[
                      {
                        label: "Treaty Closing Note",
                        value: "0",
                      },
                      ...(portFolioCond
                        ? [
                            {
                              label: "Portfolio Statement",
                              value: "Portfolio Statement",
                            },
                          ]
                        : []),
                    ]}
                    {...form}
                  />
                </div>
                <Show
                  if={!form.values.doc_type?.includes("Portfolio Statement")}
                >
                  <div>
                    <RegularSelectInput
                      id="statement_type"
                      label="Quarter"
                      placeholder="Select Quarter"
                      options={[
                        { label: "All Quarters", value: "All" },
                        ...(treaty?.treaty_accounts?.map((el: any) => ({
                          label: _.get(
                            AccountPeriodsNames,
                            el?.account_periods || ""
                          ),
                          value: el?.treaty_account_id || "",
                        })) || []),
                      ]}
                      {...form}
                    />
                  </div>
                </Show>
              </div>
            </div>
          </div>
        }
      />
    </Modal>
  );
}
